import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { KEYCHAIN_PRODUCT_ID } from '../../constants';

/**
 * DoubleSidedHeadSelector
 *
 * Renders a single select for choosing which face of a double-sided head
 * is shown on the keychain product (only the front matters on a keychain),
 * and double-sided heads if a keychain product is currently seeclted in build.
 * Outputs a hidden input so the value is persisted as a line item property.
 *
 * @author Matt Haynes <Matthew.haynes@four.agency>
 * @package Minifigs
 */

const DOUBLE_SIDED_COLLECTION_HANDLE = 'double-sided-heads-for-keychain-notification';

const DoubleSidedHeadSelector = ({
    product,
    selectedHeadProduct = null,
    selectedKeychainProduct = null,
    activeSection = '',
}) => {
    const isKeychainMode = product?.id === KEYCHAIN_PRODUCT_ID;
    const isHeadMode = activeSection === 'heads' && selectedKeychainProduct?.id === KEYCHAIN_PRODUCT_ID;

    const headProductForCollectionCheck = isKeychainMode ? selectedHeadProduct : product;

    const isDoubleSided = useMemo(
        () =>
            headProductForCollectionCheck?.collections?.edges?.some(
                (edge) => edge?.node?.handle === DOUBLE_SIDED_COLLECTION_HANDLE
            ) || false,
        [headProductForCollectionCheck]
    );

    const shouldRender = useMemo(() => {
        if (isKeychainMode) {
            return !!(selectedHeadProduct && isDoubleSided);
        }
        if (isHeadMode) {
            return !!(selectedKeychainProduct && isDoubleSided);
        }
        return false;
    }, [isKeychainMode, isHeadMode, selectedHeadProduct, selectedKeychainProduct, isDoubleSided]);

    const options = useMemo(() => {
        if (!shouldRender) return [];
        const primarySource = isKeychainMode ? selectedHeadProduct : product;
        const secondarySource = isKeychainMode ? product : selectedHeadProduct;
        const labelsRaw =
            primarySource?.doubleSidedHeadTextLabels?.value ||
            secondarySource?.doubleSidedHeadTextLabels?.value ||
            null;
        let parsedOptions = [];
        if (labelsRaw) {
            try {
                const parsed = JSON.parse(labelsRaw);
                if (Array.isArray(parsed)) {
                    parsedOptions = parsed.filter((v) => typeof v === 'string' && v.trim().length);
                }
            } catch (e) {
                console.warn('DoubleSidedHeadSelector: failed to parse labels', labelsRaw, e);
            }
        }
        if (parsedOptions.length < 2) {
            parsedOptions = ['Front', 'Back'];
        }
        return parsedOptions;
    }, [shouldRender, product, selectedHeadProduct, isKeychainMode]);

    const [front, setFront] = useState(options[0] || '');

    useEffect(() => {
        if (!options.length) return;
        setFront((prev) => (options.includes(prev) ? prev : options[0]));
    }, [options]);

    if (!shouldRender) return null; // Render only when conditions met

    return (
        <div className="double-sided-head-selector">
            <h3>Face to display</h3>
            <p className="double-sided-head-note">
                {isHeadMode
                    ? 'This head has two faces and will be used on a keychain. Choose which face should show.'
                    : 'Your selected head has two faces. Choose which face you want showing on the keychain.'}
            </p>
            <label className="double-sided-head-field">
                <select value={front} onChange={(e) => setFront(e.target.value)} name="doubleSidedHeadFront">
                    {options.map((opt) => (
                        <option key={`front-${opt}`} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            </label>
            <input
                type="hidden"
                name="properties[_doubleSidedHeadFront]"
                value={front}
                readOnly
                data-testid="double-sided-head-front"
            />
        </div>
    );
};

DoubleSidedHeadSelector.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string.isRequired,
        doubleSidedHeadTextLabels: PropTypes.shape({ value: PropTypes.string }),
    }).isRequired,
    selectedHeadProduct: PropTypes.shape({
        collections: PropTypes.shape({
            edges: PropTypes.arrayOf(
                PropTypes.shape({
                    node: PropTypes.shape({ handle: PropTypes.string }),
                })
            ),
        }),
        doubleSidedHeadTextLabels: PropTypes.shape({ value: PropTypes.string }),
    }),
    selectedKeychainProduct: PropTypes.shape({
        id: PropTypes.string,
        doubleSidedHeadTextLabels: PropTypes.shape({ value: PropTypes.string }),
    }),
    activeSection: PropTypes.string,
};

export default DoubleSidedHeadSelector;
